import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@foris/avocado-suite";
import { AppContext } from "../../context/EditSessionsContext";
import { Section, Session } from "@models/ISchema";
import { Types, EditedSession } from "../../context/formData.reducer";
import { ContextEDH } from "@context/ContextEDH";
import { partitionSectionsByPast } from "../../utils/partitionSectionsByPast";
import { usePastSessionsVisibilityStore } from "../../context/usePastSessionsVisibilityStore";
import SessionsAccordion from "../sessions-accordion";
import styles from "./sectionsList.module.scss";

interface SectionsListProps {
  sections: Section[];
  onCompareBySession: (value: Session) => void;
  onCompareBySection: (
    sessions: Session[],
    section: Section,
    checked: boolean,
    newSessions: EditedSession[],
  ) => boolean;
  userCanCreate: boolean;
}

const SectionsList = ({
  sections,
  onCompareBySection,
  onCompareBySession,
  userCanCreate,
}: SectionsListProps) => {
  const { state, dispatch } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const showPast = usePastSessionsVisibilityStore(s => s.showPast);
  const toggleShowPast = usePastSessionsVisibilityStore(s => s.toggleShowPast);
  const { t } = useTranslation();
  const sessionsToDelete = state?.form?.sessionsToDelete ?? {};

  const thresholdDate = state?.form?.blockingThreshold;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;

  const { visibleSections, pastSections, onlyPastSections } = useMemo(
    () => partitionSectionsByPast(sections, thresholdDate, canEditPastSessions),
    [sections, thresholdDate, canEditPastSessions],
  );

  const switchChecked = onlyPastSections || showPast;
  const renderPastSections = switchChecked && pastSections.length > 0;

  const getMockSession = (section: Section) => ({
    id: section.id + (section?.sessions?.length || 0),
    blocks: null,
    instructors: null,
    classrooms: null,
    intervals: null,
    session: null,
    isNew: true,
    section: section,
  });

  const sessionsToCreateBySection = useMemo(() => {
    if (!state?.form?.sessionsToCreate?.length) {
      return {};
    }

    const result: { [key: string]: EditedSession[] } = {};
    state?.form?.sessionsToCreate?.forEach(session => {
      const sectionId = session.section?.id;

      if (sectionId) {
        if (!result[sectionId]) result[sectionId] = [];

        result[sectionId].push(session);
      }
    });

    return result;
  }, [state?.form?.sessionsToCreate]);

  const renderSectionAccordion = (section: Section, index: number) => {
    const emptyNewSession =
      state?.form?.selectedCreateSession && state?.form?.selectedSection?.id === section.id
        ? [getMockSession(section)]
        : [];

    return (
      <SessionsAccordion
        key={section.id}
        colorIndex={index + 1}
        section={section}
        savedSessions={state?.form?.savedSessions}
        newSessions={sessionsToCreateBySection?.[section?.id] ?? []}
        selectedSessions={state?.form?.selectedSessions}
        mockSessionsToCreate={emptyNewSession}
        sessionsToDelete={sessionsToDelete}
        linkWeeks={state?.link?.weeks}
        canCreateSessions={userCanCreate}
        isCreatingSession={state?.form?.selectedCreateSession}
        onSessionClick={session => {
          onCompareBySession(session as Session);
        }}
        onCreateSession={() => {
          dispatch({ type: Types.SelectedCreateSession, payload: section });
        }}
        onChangeSelectAll={(sessions, section, newSessions, checked) => {
          onCompareBySection(sessions, section, checked, newSessions);
        }}
      />
    );
  };

  return (
    <div className={styles.sectionsList}>
      {visibleSections.map((section, index) => renderSectionAccordion(section, index))}

      {pastSections.length > 0 && (
        <Switch
          className={styles.pastSessionsToggle}
          checked={switchChecked}
          disabled={onlyPastSections}
          onChange={toggleShowPast}
          label={t("assignation-edit.past-sessions.toggle-label")}
        />
      )}

      {renderPastSections &&
        pastSections.map((section, index) =>
          renderSectionAccordion(section, visibleSections.length + index),
        )}
    </div>
  );
};

export default SectionsList;
